type Tone =
  | 'success'
  | 'warning'
  | 'info'
  | 'shipped'
  | 'danger'
  | 'premium'
  | 'returned'
  | 'neutral';

// v2 badges: soft fill + the matching ink (doc §07).
const toneClass: Record<Tone, string> = {
  success: 'badge-success',
  warning: 'badge-warning',
  info: 'badge-info',
  shipped: 'badge-accent',
  danger: 'badge-danger',
  premium: 'badge-warning',
  returned: 'badge-jamun',
  neutral: '',
};

// Maps the order/seller/ticket status vocabulary onto a semantic tone.
const statusTone: Record<string, Tone> = {
  active: 'success',
  approved: 'success',
  delivered: 'success',
  verified: 'success',
  paid: 'success',
  resolved: 'success',
  online: 'success',
  completed: 'success',
  pending: 'warning',
  'in review': 'warning',
  'in progress': 'warning',
  'low stock': 'warning',
  processing: 'info',
  placed: 'info',
  open: 'info',
  accepted: 'info',
  packed: 'info',
  claimed: 'info',
  shipped: 'shipped',
  'ready for delivery': 'shipped',
  'picked up': 'shipped',
  'out for delivery': 'shipped',
  'en route': 'shipped',
  cancelled: 'danger',
  failed: 'danger',
  rejected: 'danger',
  suspended: 'danger',
  blocked: 'danger',
  flagged: 'danger',
  vip: 'premium',
  featured: 'premium',
  gold: 'premium',
  returned: 'returned',
  refunded: 'returned',
  draft: 'neutral',
  inactive: 'neutral',
  paused: 'neutral',
  offline: 'neutral',
};

/**
 * Sentence case for display: `OUT_FOR_DELIVERY` / `Out For Delivery` become
 * `Out for delivery`. Short all-caps acronyms (VIP, COD, KYC) are kept.
 */
function sentenceCase(s: string): string {
  const spaced = s.replace(/_/g, ' ').trim();
  if (/^[A-Z]{2,4}$/.test(spaced)) return spaced;
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Status pill — design system v2 §07 `.badge`: 24px pill, 12.5/600, soft tone. */
export function StatusBadge({ status, dot = false }: { status: string; dot?: boolean }) {
  const key = status.replace(/_/g, ' ').toLowerCase();
  const tone = statusTone[key] ?? 'neutral';
  return (
    <span className={`badge ${dot ? 'badge-dot' : ''} ${toneClass[tone]}`}>
      {sentenceCase(status)}
    </span>
  );
}
